import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { apiGet, apiPost, getAuthHeaders } from '../../lib/api'
import { 
  Award, 
  Plus, 
  Search, 
  Filter, 
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  Archive,
  MoreVertical,
  Camera,
  Edit3,
  Download,
  Printer,
  X
} from 'lucide-react'

interface Certification {
  id: string
  certName: string
  issuer: string
  issueDate: string | null
  expirationDate: string | null
  status: 'active' | 'expiring_soon' | 'expired' | 'archived'
  documentUri?: string
  certNumber?: string
  state?: string
}

type FilterType = 'all' | 'active' | 'expiring' | 'expired' | 'archived'

export default function CertificationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [selectionMode, setSelectionMode] = useState<'export' | 'print' | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isPreparing, setIsPreparing] = useState(false)

  const { data: certificationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['/api/certifications'],
    queryFn: () => apiGet<{ certifications: Certification[] }>('/certifications'),
  })

  if (error) {
    console.error('Failed to load certifications:', error)
  }

  const rawCertifications = certificationsData?.certifications || []

  const getComputedStatus = (cert: Certification): Certification['status'] => {
    if (cert.status === 'archived') return 'archived'
    if (!cert.expirationDate) return 'active'
    
    const now = new Date()
    const expDate = new Date(cert.expirationDate)
    const diffMs = expDate.getTime() - now.getTime()
    const daysUntilExpiration = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (daysUntilExpiration < 0) return 'expired'
    if (daysUntilExpiration <= 90) return 'expiring_soon'
    return 'active'
  }

  const certifications = rawCertifications.map(cert => ({
    ...cert,
    status: getComputedStatus(cert)
  }))

  const sortedCertifications = [...certifications].sort((a, b) => {
    if (!a.expirationDate && !b.expirationDate) return 0
    if (!a.expirationDate) return 1
    if (!b.expirationDate) return -1
    return new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime()
  })

  const filteredCertifications = sortedCertifications.filter(cert => {
    const matchesSearch = (cert.certName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (cert.issuer || '').toLowerCase().includes(searchQuery.toLowerCase())
    
    if (!matchesSearch) return false
    
    if (filter === 'all') return cert.status !== 'archived'
    if (filter === 'active') return cert.status === 'active'
    if (filter === 'expiring') return cert.status === 'expiring_soon'
    if (filter === 'expired') return cert.status === 'expired'
    if (filter === 'archived') return cert.status === 'archived'
    return true
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        )
      case 'expiring_soon':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            Expiring Soon
          </span>
        )
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            <AlertTriangle className="w-3 h-3" />
            Expired
          </span>
        )
      case 'archived':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            <Archive className="w-3 h-3" />
            Archived
          </span>
        )
      default:
        return null
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No expiration'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getTimeUntilExpiration = (dateString: string | null) => {
    if (!dateString) return { text: '', color: '' }
    const now = new Date()
    const expDate = new Date(dateString)
    const diffMs = expDate.getTime() - now.getTime()
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffMs < 0) {
      const pastMs = Math.abs(diffMs)
      const days = Math.floor(pastMs / (1000 * 60 * 60 * 24))
      let text = ''
      if (days < 30) text = `${days} day${days !== 1 ? 's' : ''} ago`
      else {
        const months = Math.floor(days / 30)
        if (months < 12) text = `${months} month${months !== 1 ? 's' : ''} ago`
        else {
          const years = Math.floor(months / 12)
          text = `${years} year${years !== 1 ? 's' : ''} ago`
        }
      }
      return { text, color: 'text-red-600' }
    }
    
    const years = Math.floor(totalDays / 365)
    const remainingDaysAfterYears = totalDays % 365
    const months = Math.floor(remainingDaysAfterYears / 30)
    const days = remainingDaysAfterYears % 30
    
    const parts: string[] = []
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`)
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`)
    if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`)
    
    const text = parts.length > 0 ? parts.join(' ') : 'today'
    let color = 'text-green-600'
    if (totalDays < 30) color = 'text-red-600'
    else if (totalDays <= 90) color = 'text-yellow-600'
    
    return { text, color }
  }

  const getCounts = () => {
    const active = certifications.filter(c => c.status === 'active').length
    const expiring = certifications.filter(c => c.status === 'expiring_soon').length
    const expired = certifications.filter(c => c.status === 'expired').length
    return { active, expiring, expired, total: certifications.filter(c => c.status !== 'archived').length }
  }

  const counts = getCounts()

  const openSelectionModal = (mode: 'export' | 'print') => {
    setSelectedIds(new Set(filteredCertifications.filter(c => c.status === 'active' || c.status === 'expiring_soon').map(c => c.id)))
    setSelectionMode(mode)
  }

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCertifications.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredCertifications.map(c => c.id)))
    }
  }

  const statusPriority = (status: string) => {
    if (status === 'active' || status === 'expiring_soon') return 0
    if (status === 'expired') return 1
    return 2
  }

  const alphabeticallySorted = [...filteredCertifications].sort((a, b) => {
    const priorityDiff = statusPriority(a.status) - statusPriority(b.status)
    if (priorityDiff !== 0) return priorityDiff
    return (a.certName || '').localeCompare(b.certName || '')
  })

  const getSelectedCerts = () => alphabeticallySorted.filter(c => selectedIds.has(c.id))

  const handleExportCSV = () => {
    const selected = getSelectedCerts()
    const rows = [
      ['Certification', 'Issuer', 'Cert Number', 'State', 'Issue Date', 'Expiration Date', 'Status'],
      ...selected.map(cert => [
        cert.certName,
        cert.issuer || '',
        cert.certNumber || '',
        cert.state || '',
        cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : '',
        cert.expirationDate ? new Date(cert.expirationDate).toLocaleDateString() : 'No expiration',
        cert.status.replace('_', ' ')
      ])
    ]
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `certifications-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setSelectionMode(null)
  }

  const handlePrint = async () => {
    setIsPreparing(true)
    const selected = getSelectedCerts()
    const imagePromises = selected.map(async (cert) => {
      if (!cert.documentUri) return { id: cert.id, dataUrl: null }
      try {
        const res = await fetch(cert.documentUri, {
          headers: getAuthHeaders(),
          credentials: 'include',
        })
        if (!res.ok) return { id: cert.id, dataUrl: null }
        const blob = await res.blob()
        return new Promise<{ id: string; dataUrl: string | null }>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve({ id: cert.id, dataUrl: reader.result as string })
          reader.onerror = () => resolve({ id: cert.id, dataUrl: null })
          reader.readAsDataURL(blob)
        })
      } catch {
        return { id: cert.id, dataUrl: null }
      }
    })

    const imageResults = await Promise.all(imagePromises)
    const imageMap = new Map(imageResults.map(r => [r.id, r.dataUrl]))

    const certCards = selected.map(cert => {
      const img = imageMap.get(cert.id)
      const statusLabel = cert.status.replace('_', ' ')
      const statusColors: Record<string, string> = {
        active: 'color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0;',
        expiring_soon: 'color: #a16207; background: #fefce8; border: 1px solid #fef08a;',
        expired: 'color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca;',
        archived: 'color: #4b5563; background: #f9fafb; border: 1px solid #e5e7eb;',
      }

      const dataFields = `
        <div class="cert-data">
          <h2>${cert.certName}</h2>
          <div class="field"><span class="label">Issuer</span><span class="value">${cert.issuer || '—'}</span></div>
          ${cert.certNumber ? `<div class="field"><span class="label">Cert #</span><span class="value">${cert.certNumber}</span></div>` : ''}
          ${cert.state ? `<div class="field"><span class="label">State</span><span class="value">${cert.state}</span></div>` : ''}
          <div class="field"><span class="label">Issue Date</span><span class="value">${cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : '—'}</span></div>
          <div class="field"><span class="label">Expiration</span><span class="value">${cert.expirationDate ? new Date(cert.expirationDate).toLocaleDateString() : 'No expiration'}</span></div>
          <div class="field"><span class="label">Status</span><span class="status-badge" style="${statusColors[cert.status] || ''}">${statusLabel}</span></div>
        </div>
      `

      if (img) {
        return `<div class="cert-card has-image"><div class="cert-image"><img src="${img}" alt="${cert.certName}" /></div>${dataFields}</div>`
      }
      return `<div class="cert-card no-image">${dataFields}</div>`
    }).join('')

    const printContent = `
      <html>
        <head>
          <title>Certifications</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 24px; }
            .cert-card { border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 20px; overflow: hidden; page-break-inside: avoid; }
            .cert-card.has-image { display: flex; }
            .cert-image { width: 50%; flex-shrink: 0; background: #f9fafb; display: flex; align-items: center; justify-content: center; padding: 16px; }
            .cert-image img { max-width: 100%; max-height: 400px; object-fit: contain; border-radius: 6px; }
            .cert-card.has-image .cert-data { width: 50%; }
            .cert-card.no-image .cert-data { width: 100%; }
            .cert-data { padding: 20px; display: flex; flex-direction: column; justify-content: center; gap: 10px; }
            .cert-data h2 { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 4px; }
            .field { display: flex; justify-content: space-between; align-items: center; }
            .label { font-size: 13px; color: #6b7280; }
            .value { font-size: 13px; font-weight: 500; color: #111; }
            .status-badge { font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 999px; text-transform: capitalize; }
            @media print { body { padding: 0; } .cert-card { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Certifications</h1>
          <p class="subtitle">Exported on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} &bull; ${selected.length} certification${selected.length !== 1 ? 's' : ''}</p>
          ${certCards}
        </body>
      </html>
    `
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(printContent)
      printWindow.document.close()
      setTimeout(() => {
        printWindow.focus()
        printWindow.print()
      }, 500)
    }
    setIsPreparing(false)
    setSelectionMode(null)
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Certifications</h3>
        <p className="text-gray-500 mb-4">
          {error instanceof Error ? error.message : 'An error occurred while loading your certifications.'}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Certifications</h1>
          <p className="text-gray-500 mt-1">Track and manage your professional certifications</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => openSelectionModal('export')}
            disabled={filteredCertifications.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-export-csv"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => openSelectionModal('print')}
            disabled={filteredCertifications.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-print"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Certification
          </button>
          
          {showAddMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowAddMenu(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <Link
                  to="/certifications/new?mode=scan"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  onClick={() => setShowAddMenu(false)}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Camera className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Scan Document</p>
                    <p className="text-xs text-gray-500">Upload and auto-extract</p>
                  </div>
                </Link>
                <Link
                  to="/certifications/new?mode=manual"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  onClick={() => setShowAddMenu(false)}
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Edit3 className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enter Manually</p>
                    <p className="text-xs text-gray-500">Type in details yourself</p>
                  </div>
                </Link>
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.active}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.expiring}</p>
              <p className="text-sm text-gray-500">Expiring</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.expired}</p>
              <p className="text-sm text-gray-500">Expired</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search certifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
          {(['all', 'active', 'expiring', 'expired', 'archived'] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Certifications List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredCertifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery || filter !== 'all' ? 'No certifications found' : 'No certifications yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || filter !== 'all' 
              ? 'Try adjusting your search or filter' 
              : 'Add your first certification to get started'}
          </p>
          {!searchQuery && filter === 'all' && (
            <Link
              to="/certifications/new?mode=scan"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Add Certification
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
          {filteredCertifications.map((cert) => (
            <Link
              key={cert.id}
              to={`/certifications/${cert.id}`}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">{cert.certName}</h3>
                  <p className="text-sm text-gray-500 truncate">{cert.issuer || 'Unknown issuer'}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {getStatusBadge(cert.status)}
                    <span className="text-xs text-gray-500">
                      Expires: {formatDate(cert.expirationDate)}
                      {cert.expirationDate && (() => {
                        const timeInfo = getTimeUntilExpiration(cert.expirationDate)
                        return <span className={timeInfo.color}> ({timeInfo.text})</span>
                      })()}
                    </span>
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {selectionMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50" onClick={() => setSelectionMode(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectionMode === 'export' ? 'Export Certifications' : 'Print Certifications'}
              </h2>
              <button onClick={() => setSelectionMode(null)} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="button-close-selection">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100">
              <p className="text-sm text-gray-500">Only active certifications are selected by default. To include expired or archived certifications, scroll to the bottom and check them.</p>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="checkbox-select-all">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredCertifications.length && filteredCertifications.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {selectedIds.size === filteredCertifications.length ? 'Deselect All' : 'Select All'}
                </span>
              </label>
              <span className="text-sm text-gray-500">{selectedIds.size} of {filteredCertifications.length} selected</span>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-2 divide-y divide-gray-100">
              {alphabeticallySorted.map(cert => (
                <label key={cert.id} className="flex items-center gap-3 py-3 cursor-pointer" data-testid={`checkbox-cert-${cert.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(cert.id)}
                    onChange={() => toggleSelection(cert.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{cert.certName}</p>
                    <p className="text-xs text-gray-500 truncate">{cert.issuer || 'Unknown issuer'}</p>
                  </div>
                  {getStatusBadge(cert.status)}
                </label>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              {isPreparing && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mr-auto">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  Preparing print page...
                </div>
              )}
              <button
                onClick={() => setSelectionMode(null)}
                disabled={isPreparing}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={selectionMode === 'export' ? handleExportCSV : handlePrint}
                disabled={selectedIds.size === 0 || isPreparing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`button-confirm-${selectionMode}`}
              >
                {isPreparing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Preparing...
                  </>
                ) : selectionMode === 'export' ? (
                  <><Download className="w-4 h-4" />Export {selectedIds.size} Certification{selectedIds.size !== 1 ? 's' : ''}</>
                ) : (
                  <><Printer className="w-4 h-4" />Print {selectedIds.size} Certification{selectedIds.size !== 1 ? 's' : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
